"use client";

import { FilterOptions } from "@/context/SearchContext";
import { SearchResult, AgreementData } from "@/types/journal";
import { Branding } from "@/types/branding";
import { getSearchToken, renewSearchToken } from "./search-token";

export { BASE_API_URL } from "./client";
import { BASE_API_URL } from "./client";

export async function getBranding(slug: string) {
  const response = await fetch(`${BASE_API_URL}/custom/${slug}/branding`);
  if (response.status !== 200) {
    return null;
  }
  return response.json() as Promise<Branding | null>;
}

export class SearchRequestError extends Error {
  constructor(public status: number) {
    super(`search request failed (${status})`);
  }
}

// GET a search endpoint with the anonymous search token (`st`, see search-token.ts). After an
// expired/invalid token rejection it renews and retries once. Throws on any other non-OK status.
async function searchFetch<T>(path: string, params: URLSearchParams): Promise<T> {
  const request = (token: string | null) => {
    const withToken = new URLSearchParams(params);
    if (token) withToken.set("st", token);
    return fetch(`${BASE_API_URL}${path}?${withToken.toString()}`);
  };

  const token = await getSearchToken();
  let response = await request(token);
  if (response.status === 401) {
    const body = await response.json().catch(() => null);
    if (body?.reason === "missing" && !token) {
      console.error(
        "The API requires a search token but this page has none: is SEARCH_TOKEN_SECRET set on the search app?",
      );
    }
    // Only these a renewal can fix; wrong_account or missing would fail again after one.
    if (
      body?.error === "search_token" &&
      (body.reason === "expired" || body.reason === "invalid")
    ) {
      const renewed = await renewSearchToken(token ?? undefined).catch(
        () => null,
      );
      if (renewed && renewed !== token) response = await request(renewed);
    }
  }
  if (!response.ok) throw new SearchRequestError(response.status);
  return response.json() as Promise<T>;
}

export interface SearchOptions {
  source?: "autocomplete";
  journalId?: string;
}

export async function getSearchResults(
  slug: string,
  searchTerm: string,
  filters: FilterOptions = {},
  options?: SearchOptions,
) {
  const params = new URLSearchParams();
  params.set("term", searchTerm);
  params.set("account", slug);
  if (options?.source === "autocomplete") {
    params.set("source", "autocomplete");
    if (options.journalId) {
      params.set("journal_id", options.journalId);
    }
  }
  // All filters including collections are sent as arrays
  for (const [key, values] of Object.entries(filters)) {
    if (!values || values.length === 0) continue;

    if (key === "quality_score") {
      // Encoded as "{system}:{value}"
      values.forEach((value: string) => {
        const colonIdx = value.indexOf(":");
        if (colonIdx > 0) {
          const system = value.slice(0, colonIdx);
          const score = value.slice(colonIdx + 1);
          params.append(
            `quality_scores_${system}[]`,
            score === "null" ? "null" : score,
          );
        }
      });
    } else {
      values.forEach((value: string) => {
        params.append(`${key}[]`, value);
      });
    }
  }

  return searchFetch<SearchResult>("/search", params);
}

export async function getSearchAutocomplete(slug: string, searchTerm: string) {
  const params = new URLSearchParams();
  params.set("account", slug);
  if (searchTerm.trim()) params.set("term", searchTerm.trim());

  return searchFetch<{
    suggestions: Array<{ value: string; id?: string }>;
  }>("/search/autocomplete", params);
}

export async function getAgreementData(
  slug: string,
  id: string,
): Promise<AgreementData | null> {
  const response = await fetch(`${BASE_API_URL}/agreements/${slug}/${id}`);
  if (response.status !== 200) return null;
  return response.json() as Promise<AgreementData>;
}

export function trackUserEvent(
  slug: string,
  eventCode: string,
  on: string,
  details?: Record<string, string | undefined>,
) {
  return fetch(`${BASE_API_URL}/events/${slug}:${eventCode}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...details, on }),
    // Lets the request finish when a tracked link navigates the page away.
    keepalive: true,
  });
}
